/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import { LimitedOrganizationInfo, UserEntity, UserIntegrations, UserTokens } from '@kemu-io/kemu-types';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../../store';
import { AsyncRequestStatus, AsyncState } from '../../../types/core_t';
import { UserAccountState, WebRTCTokenPair } from '../../../types/user';
import { trackUserEventReducer } from './trackEventReducer';
import { fetchUserProfileReducer } from './fetchUserReducer';
import { updateUserSettingsReducer } from './updateUserSettingsReducer';
import { updateUserOnboardingReducer } from './updateUserOnboardingReducer';
import { fetchWebRTCSessionTokenReducer } from './fetchWebRTCTokenReducer';
import localStorageManager from '@common/localStorageManager';

export type UserInfo = {
	profile?: UserEntity;
	organization?: LimitedOrganizationInfo;
	tokens?: UserTokens;
	// apiKey?: string;
	accountState?: UserAccountState;
	/** @deprecated no longer used, use user properties instead */
	integrations: UserIntegrations;
	asyncState: AsyncState;
}


/** List of onboarding videos */
export type UserOnboarding = {
	welcomeMessage?: boolean;
	basics?: boolean;
	marketplaceAndServices?: boolean;
	debuggingAndScripting?: boolean;
}


export type PropertiesMap = {
	// firstTimeUser: boolean;
	watchedVideos: UserOnboarding;
	onboardingModalVisible: boolean;
	videoTooltipShown: boolean;
};

export interface CustomerState {
	currentUserProfile: UserInfo,
	sessionProperties: PropertiesMap;
	webRTCSession:{
		token?: WebRTCTokenPair;
		asyncState: AsyncState;
	}
}

export type LogicMapperDockingMode = 'left' | 'right' | 'full';

export type UserSettings = {
	logicMapperDocking: LogicMapperDockingMode;
	useWidgetBundles?: boolean;
};


const getSessionProperties = (): PropertiesMap => {
	const session = localStorageManager.getStoredObject<PropertiesMap>('sessionProperties', {
		watchedVideos: {},
		onboardingModalVisible: true,
		videoTooltipShown: false,
		// firstTimeUser: true,
	});

	return {
		watchedVideos: session.watchedVideos,
		onboardingModalVisible: session.onboardingModalVisible,
		videoTooltipShown: session.videoTooltipShown,
		// firstTimeUser: session.firstTimeUser,
	}
}

// Action:Noun
export type UserActionEvent = `${string}:${string}`;

const defaultAsyncState: AsyncState = { status: AsyncRequestStatus.idle };

const initialState: CustomerState = {
	currentUserProfile: {
		asyncState: defaultAsyncState,
		integrations: {}
	},
	sessionProperties: getSessionProperties(),

	webRTCSession: {
		asyncState: defaultAsyncState
	},
};


export const marketplaceSlice = createSlice({
	name: 'customer',
	initialState,
	reducers: {
		clearWebRTCSession: (state) => {
			state.webRTCSession.asyncState.status = AsyncRequestStatus.idle;
			state.webRTCSession.asyncState.error = undefined;
			state.webRTCSession.token = undefined;
		},
		setUserProfile: (state, action: PayloadAction<{user: UserEntity, organization: LimitedOrganizationInfo}>) => {
			state.currentUserProfile.profile = action.payload.user;
			state.currentUserProfile.organization = action.payload.organization;
		},
		updateOrganizationAction: (state, action: PayloadAction<LimitedOrganizationInfo>) => {
			state.currentUserProfile.organization = action.payload;
		},
		/**
		 * Sets a session property in the local storage
		 */
		setSessionProperty: (state, action: PayloadAction<Partial<PropertiesMap>>) => {
			state.sessionProperties = {
				...state.sessionProperties,
				...action.payload,
			};

			localStorageManager.updateObject('sessionProperties', state.sessionProperties);
		},
	},

	extraReducers: (builder) => {
		trackUserEventReducer(builder);
		fetchUserProfileReducer(builder);
		fetchWebRTCSessionTokenReducer(builder);
		updateUserSettingsReducer(builder);
		updateUserOnboardingReducer(builder);
	}
});


export const {
	clearWebRTCSession,
	setUserProfile,
	setSessionProperty,
	updateOrganizationAction,
} = marketplaceSlice.actions;


export const selectSignedUserProfile = (state: RootState): UserInfo => state.user.currentUserProfile;
export const webRTCSession = (state: RootState) => state.user.webRTCSession;
/** @deprecated no longer used, use user properties instead */
export const selectUserIntegrations = (state: RootState) => state.user.currentUserProfile.integrations;
/** Retrieves the session properties from the local storage */
export const selectSessionProperties = (state: RootState): PropertiesMap => state.user.sessionProperties;
export const selectUserSettings = (state: RootState): UserSettings | null =>
	state.user.currentUserProfile.integrations.posthog?.properties?.settings as UserSettings || null;

// export const selectUserOnboarding = (state: RootState): UserOnboarding | null =>
// 	state.user.currentUserProfile.integrations.posthog?.properties?.onboarding as UserOnboarding || null;

export default marketplaceSlice.reducer;
